/**
 * The signing ceremony as one function: ask the browser to sign, send the
 * answer, and say what happened in the terms the page renders.
 *
 * The browser and the network are passed in, so every branch is unit tested
 * without either. The page owns nothing but the stage it shows.
 */
import {
  classifyCeremonyError,
  toRequestOptions,
  type AssertionFields,
} from "modules/members/webauthn";
import type {
  SignedRequest,
  StepUpChallenge,
} from "state/members/stepUp";
import {
  failureMessage,
  VERIFY_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

export type SignOutcome =
  | { kind: "signed"; request: SignedRequest }
  /** The ceremony was aborted. Nothing was sent; start again quietly. */
  | { kind: "cancelled" }
  /**
   * The browser declined without saying why: the prompt was closed or timed
   * out, or this device has no passkey for the account. Nothing was sent.
   */
  | { kind: "not_allowed" }
  | { kind: "expired" }
  /** Someone (perhaps this person, on another device) already used it. */
  | { kind: "used"; message: string }
  | { kind: "failed"; message: string };

export type SignDeps = {
  getAssertion: (
    options: PublicKeyCredentialRequestOptions,
  ) => Promise<AssertionFields>;
  verify: (
    challengeId: string,
    assertion: AssertionFields,
    actionAssigneeId?: string | null,
    comments?: string | null,
  ) => Promise<SignedRequest>;
  /** Re-reads the challenge after a failed verify, to learn its status. */
  reread: (challengeId: string) => Promise<StepUpChallenge>;
};

export const BROWSER_FAILED_MESSAGE =
  "Your browser could not use a passkey here. Try again, or try another browser.";

export const signChallenge = async (
  challenge: StepUpChallenge,
  options: {
    timeoutMs?: number;
    actionAssigneeId?: string | null;
    /** An approver's note, sent with the answer. */
    comments?: string | null;
  },
  deps: SignDeps,
): Promise<SignOutcome> => {
  let assertion: AssertionFields;
  try {
    assertion = await deps.getAssertion(
      toRequestOptions(challenge.publicKey, options.timeoutMs),
    );
  } catch (error) {
    const problem = classifyCeremonyError(error);
    if (problem === "cancelled") return { kind: "cancelled" };
    if (problem === "not_allowed") return { kind: "not_allowed" };
    return { kind: "failed", message: BROWSER_FAILED_MESSAGE };
  }

  try {
    const request = await deps.verify(
      challenge.id,
      assertion,
      options.actionAssigneeId,
      options.comments,
    );
    return { kind: "signed", request };
  } catch (error) {
    const message = failureMessage(error, VERIFY_FAILED_MESSAGE);
    // The server words "expired" and "already used" the same way, on purpose.
    // The challenge's own status tells them apart, and also covers a verify
    // whose answer was lost on the way back.
    const latest = await deps.reread(challenge.id).catch(() => null);
    if (latest?.status === "expired") return { kind: "expired" };
    if (latest?.status === "consumed" || latest?.status === "cancelled") {
      return { kind: "used", message };
    }
    return { kind: "failed", message };
  }
};
